import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLocalization } from "@fluent/react";
import { Alert, Button, Row } from "@narmi/design_system";
import { entities, modules } from "byzantine";
import type { InvoiceRecipientMatchDisplayState } from "byzantine/src/dbbl/businessLogic/entities/invoices/helpers";
import RecipientSelectorWithDrawer from "../RecipientSelectorWithDrawer";
import useInstitutionName from "../../../src/modules/recipientDrawer/sections/paymentMethods/useInstitutionName";
import styles from "./InvoiceContainer.module.scss";
import { INVOICE_ROUTE, INVOICE_DETAILS_ROUTE } from "./InvoiceRoutes";
import { useInvoiceFlow } from "./InvoiceFlowContext";
import type { RecipientCardFields } from "./RecipientCard";

const RecipientStep = () => {
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const { invoice, savedRecipientId, setSavedRecipientId } = useInvoiceFlow();
  const hasManageRecipientPermission =
    modules.achPayments.useManageRecipientPermission();
  const recipientForm = entities.recipients.ACHPaymentRecipientForm.useForm();
  const {
    values: { selectedRecipientId },
  } = recipientForm;
  const selectedRecipient = entities.recipients.useSelectedRecipient();
  const isSelectedRecipientUnpayable = Boolean(
    selectedRecipient && !selectedRecipient.ach_destination
  );

  const [isCleared, setIsCleared] = useState(false);
  // the drawer lives inside RecipientSelectorWithDrawer, but the affordances
  // that open it live out here, so this screen owns whether it's open
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // set when the user hits Next while something still blocks the flow
  const [showProceedError, setShowProceedError] = useState(false);

  const { createRecipientFromInvoice, loading: isSaving } =
    entities.invoices.useCreateRecipientFromInvoice();

  const extracted = invoice?.extracted;

  const summary = entities.invoices.getInvoiceRecipientMatchSummary(
    entities.invoices.toInvoiceRecipientMatchSummaryInput(invoice)
  );

  const { state } = summary;

  const isNewRecipientState = state === "newRecipient";

  const extractedAccountNumber = extracted?.remittance_account?.value ?? null;
  const extractedRoutingNumber = extracted?.remittance_routing?.value ?? null;
  const extractedRecipientName = extracted?.recipient_name?.value ?? null;

  const { primaryCandidate } = summary;

  // the invoice extraction gives us bank details but no FI name, so resolve it
  // from the routing number the same way the recipient drawer does
  const { institutionName: extractedInstitutionName } = useInstitutionName(
    extractedRoutingNumber,
    "ach"
  );

  const fields: RecipientCardFields | null =
    extractedAccountNumber || extractedRoutingNumber
      ? {
          ...(extractedInstitutionName
            ? { financialInstitution: extractedInstitutionName }
            : {}),
          accountNumber: extractedAccountNumber || "",
          routingNumber: extractedRoutingNumber || "",
        }
      : null;

  const isMatchedState = state === "matchedBankDetails";
  const isNoMatch = state === "recipientUnavailable";

  const canSaveRecipient = Boolean(
    extractedRecipientName && extractedAccountNumber && extractedRoutingNumber
  );

  const hasExtractedRecipientDetails = Boolean(
    extractedRecipientName || extractedAccountNumber || extractedRoutingNumber
  );
  const isUnavailableWithExtractedDetails =
    state === "recipientUnavailable" && hasExtractedRecipientDetails;

  // This flow always shows a card when there is a recipient to show: a selected
  // recipient, or the values read off the invoice. The picker only appears when
  // there is neither — nothing matched, or the user closed the card.
  const hasSelectedRecipient = Boolean(selectedRecipientId);
  const hasInvoiceCard =
    !hasSelectedRecipient &&
    !isCleared &&
    (isNewRecipientState ||
      state === "needsReview" ||
      (isUnavailableWithExtractedDetails && hasManageRecipientPermission));
  const isNewRecipient = hasInvoiceCard && isNewRecipientState;
  const isDetailsMissing =
    hasInvoiceCard &&
    (state === "needsReview" || isUnavailableWithExtractedDetails);

  // only a bank-details match is safe to select on the user's behalf
  const autoSelectedRecipientId =
    state === "matchedBankDetails"
      ? primaryCandidate?.recipient_uuid
      : undefined;

  // Either the card still needs review, or the selected recipient was created
  // without ACH details. The form only checks that an id is set, so neither is
  // caught by validation.
  const needsRecipientDetails =
    isDetailsMissing || isSelectedRecipientUnpayable;

  // The single answer to "can the user move on?", so Next and the error banner
  // can never disagree — and the banner clears itself as soon as the reason
  // stops applying.
  let proceedError: string | null = null;
  if (isNewRecipient) {
    // extracted off the invoice but not yet a real recipient
    proceedError = l10n.getString("invoice-recipient-unsaved-error");
  } else if (needsRecipientDetails) {
    proceedError = l10n.getString("invoice-recipient-details-missing-error");
  }

  // A bank-details match is an existing recipient we can trust, so select it
  // into the form. That satisfies the form's required `selectedRecipientId` and
  // hands rendering to RecipientSelectorWithDrawer. Closing the card sets
  // `isCleared`, which stops it coming straight back.

  useEffect(() => {
    if (!autoSelectedRecipientId || hasSelectedRecipient || isCleared) return;
    recipientForm.setFieldValue(
      "selectedRecipientId",
      autoSelectedRecipientId as API.RecipientId
    );
  }, [autoSelectedRecipientId, hasSelectedRecipient, isCleared, recipientForm]);

  const onCancel = () => navigate("/transfer");

  // the selector renders its own card, so it owns the badge for the states that
  // reach it: an unpayable recipient, the one we just created, and the backend
  // bank-details match
  let cardState: InvoiceRecipientMatchDisplayState | undefined;
  if (isSelectedRecipientUnpayable) {
    cardState = "recipientUnavailable";
  } else if (selectedRecipientId && selectedRecipientId === savedRecipientId) {
    cardState = "savedRecipient";
  } else if (
    selectedRecipientId &&
    selectedRecipientId === autoSelectedRecipientId &&
    isMatchedState
  ) {
    cardState = "matchedBankDetails";
  }

  const handleClearCard = () => {
    setSaveError(null);
    setShowProceedError(false);
    recipientForm.setFieldValue("selectedRecipientId", "");
    setIsCleared(true);
  };

  // opens the drawer seeded with whatever we read off the invoice, so the user
  // only fills the gaps
  const handleOpenRecipientDrawer = () => {
    setSaveError(null);
    setShowProceedError(false);
    setIsDrawerOpen(true);
  };

  const handleDrawerOpenChange = (isOpen: boolean) => {
    setIsDrawerOpen(isOpen);
  };

  // the invoice never gives us an account type, and invoice payees are
  // businesses, so default it rather than leaving the user a required blank
  const invoiceValues = {
    name: extractedRecipientName,
    accountNumber: extractedAccountNumber,
    routingNumber: extractedRoutingNumber,
    accountType: entities.recipients.ACCOUNT_TYPE_OPTIONS.BUSINESS_CHECKING,
    type: "business",
  };

  // Only seed a recipient this flow is creating. Seeding one the user picked
  // from the dropdown would attach the invoice's account and routing numbers to
  // an unrelated saved recipient the moment they hit save.
  const isInvoiceOwnRecipient =
    !hasSelectedRecipient || selectedRecipientId === savedRecipientId;
  const initialValuesFromInvoice = isInvoiceOwnRecipient
    ? invoiceValues
    : undefined;

  const handleSaveNewRecipient = () => {
    if (!canSaveRecipient) return;
    setSaveError(null);
    setShowProceedError(false);
    createRecipientFromInvoice(
      {
        name: extractedRecipientName as string,
        accountNumber: extractedAccountNumber as string,
        routingNumber: extractedRoutingNumber as string,
      },
      {
        onSuccess: (recipient) => {
          recipientForm.setFieldValue("selectedRecipientId", recipient.id);
          setSavedRecipientId(recipient.id);
        },
        onError: (errorMessage) => setSaveError(errorMessage ?? ""),
      }
    );
  };

  const handleNext = async () => {
    if (proceedError) {
      setShowProceedError(true);
      return;
    }
    const { success } = await recipientForm.submitForm();
    if (success) {
      navigate(`/transfer/${INVOICE_ROUTE}/${INVOICE_DETAILS_ROUTE}`);
    }
  };

  return (
    <>
      <div className={`${styles.detailsColumn} ${styles.stepFadeIn}`}>
        <h1 className="fontFamily--heading fontSize--heading1">
          {l10n.getString("invoice-review-title")}
        </h1>
        <p className="fontColor--secondary margin--top--s">
          {l10n.getString("invoice-review-blurb")}
        </p>

        <div className="margin--top--xl">
          {showProceedError && proceedError && (
            <div className="margin--bottom--l">
              <Alert kind="error" isActive isDismissable={false}>
                {proceedError}
              </Alert>
            </div>
          )}

          <Row alignItems="center" justifyContent="space-between">
            <Row.Item>
              <h4 className="fontSize--m nds-sans">
                {l10n.getString("label-recipient")}
              </h4>
            </Row.Item>
            <Row.Item shrink>
              {isNewRecipient && hasManageRecipientPermission && (
                <Button
                  label={l10n.getString("invoice-recipient-save-to-continue")}
                  onClick={handleSaveNewRecipient}
                  isLoading={isSaving}
                  size="xs"
                />
              )}
              {needsRecipientDetails && hasManageRecipientPermission && (
                <Button
                  kind="tonal"
                  label={l10n.getString("invoice-recipient-review-to-continue")}
                  onClick={handleOpenRecipientDrawer}
                  size="xs"
                  endIcon="arrow-right"
                />
              )}
            </Row.Item>
          </Row>

          {/* the invoice had nothing to offer, but that stops mattering the
              moment the user picks someone themselves */}
          {isNoMatch && !hasSelectedRecipient && !hasInvoiceCard && (
            <div className="margin--top--s">
              <Alert kind="warn" isActive isDismissable={false}>
                {l10n.getString("invoice-recipient-no-match-warning", {
                  canManageRecipient: String(hasManageRecipientPermission),
                })}
              </Alert>
            </div>
          )}

          {saveError !== null && (
            <div className="margin--top--s">
              <Alert kind="error" isActive isDismissable={false}>
                {saveError || l10n.getString("invoice-recipient-save-error")}
              </Alert>
            </div>
          )}

          <div className="margin--top--s">
            <RecipientSelectorWithDrawer
              label={l10n.getString("single-ach-payment-recipient-send-to")}
              showSelectedCard
              onChange={(recipientId) => {
                setShowProceedError(false);
                if (!recipientId) setIsCleared(true);
              }}
              // badge the recipient this flow created off the invoice, and
              // the one the backend matched on bank details
              {...(cardState ? { cardState } : {})}
              // with no recipient selected the invoice's own values are still
              // worth showing, so the picker is the last resort rather than the
              // default
              {...(hasInvoiceCard
                ? {
                    fallbackCard: {
                      title: summary.title,
                      fields,
                      state,
                      ...(isNewRecipient && hasManageRecipientPermission
                        ? { onEdit: handleOpenRecipientDrawer }
                        : {}),
                      onClear: handleClearCard,
                    },
                  }
                : {})}
              isDrawerOpen={isDrawerOpen}
              onDrawerOpenChange={handleDrawerOpenChange}
              {...(initialValuesFromInvoice
                ? { initialValuesFromInvoice }
                : {})}
              onRecipientCreated={setSavedRecipientId}
              keepRecipientWithoutAchDetails
              drawerNeedsReview={needsRecipientDetails}
              // only highlight the field as an error for a real backend
              // no-match, not when the user closed the card themselves
              error={
                isNoMatch && !hasSelectedRecipient && !hasInvoiceCard
                  ? l10n.getString("invoice-recipient-required-error")
                  : undefined
              }
            />
          </div>
        </div>
      </div>

      <div className={styles.actionsRow}>
        <Row alignItems="center" justifyContent="end">
          <Row.Item shrink>
            <Button
              kind="plain"
              label={l10n.getString("button-cancel")}
              onClick={onCancel}
            />
          </Row.Item>
          <Row.Item shrink>
            <Button
              label={l10n.getString("button-next")}
              onClick={handleNext}
            />
          </Row.Item>
        </Row>
      </div>
    </>
  );
};

export default RecipientStep;
